import {
  Grid,
  TextField,
  InputAdornment,
  Button,
  CircularProgress,
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import {
  IconBuildingWarehouse,
  IconMapPin,
  IconShip,
  IconUpload,
} from "@tabler/icons-react";
import MainCard from "../../../components/common/MainCard";
import SelectionMessage from "./SelectionMessage";
import CostosContainer from "./CostosContainer";
import { useFormik } from "formik";
import * as yup from "yup";
import { useState } from "react";
import {
  useGetContainersQuery,
  useGetDestinationsQuery,
  useGetOriginsQuery,
  useGetProvidersQuery,
  useLazyGetCostsQuery,
  useProcessFileMutation,
} from "../../../services/costs/costsService";
import CustomDialog from "../../../components/common/dialogs/CustomDialog";
import UploadXlsx from "./UploadXlsx";
import { useAppSelector } from "../../../store/hooks";

const schema = yup.object().shape({
  naviero: yup.string().nullable(),
  origen: yup.string().nullable().required("Campo Requerido"),
  destino: yup.string().nullable().required("Campo Requerido"),
  contenedor: yup.string().nullable(),
});

const Cotizaciones = () => {
  const { userInfo } = useAppSelector((state) => state.auth);
  const [keyAutocomplete, setKeyAutcomplete] = useState(1);
  const [showModalUpload, setShowModalUpload] = useState(false);
  const [processFile, { isLoading, data, isError, isSuccess, reset }] =
    useProcessFileMutation();

  const { isLoading: isLoadingProviders, data: dataProviders } =
    useGetProvidersQuery(undefined, { refetchOnMountOrArgChange: true });
  const { isLoading: isLoadingOrigins, data: dataOrigins } = useGetOriginsQuery(
    undefined,
    { refetchOnMountOrArgChange: true }
  );
  const { isLoading: isLoadingDestinations, data: dataDestinations } =
    useGetDestinationsQuery(undefined, { refetchOnMountOrArgChange: true });
  const { isLoading: isLoadingContainers, data: dataContainers } =
    useGetContainersQuery(undefined, { refetchOnMountOrArgChange: true });
  const [
    getCosts,
    { isLoading: isLoadingCosts, isFetching: isFetchingCosts, data: dataCosts },
  ] = useLazyGetCostsQuery();

  // Filtering costs to get only items where sum per_unit is different to 0
  const _costs =
    dataCosts?.datos.filter(
      (c) => c.charges.reduce((acc, cur) => acc + Number(cur.per_unit), 0) !== 0
    ) || [];

  const providers =
    dataProviders?.datos.map((pr) => ({
      label: pr.nombre,
      value: pr.nombre,
    })) || [];

  const origins =
    dataOrigins?.datos.map((org) => ({
      label: org.nombre,
      value: org.nombre,
    })) || [];

  const destinations =
    dataDestinations?.datos.map((des) => ({
      label: des.nombre,
      value: des.nombre,
    })) || [];

  const containers =
    dataContainers?.datos.map((con) => ({
      label: con.nombre,
      value: con.nombre,
    })) || [];

  const { setFieldValue, errors, touched, handleSubmit, resetForm } = useFormik(
    {
      initialValues: {
        naviero: null,
        origen: null,
        destino: null,
        contenedor: null,
      },
      validationSchema: schema,
      onSubmit: (values) => {
        getCosts({
          container: values.contenedor || "",
          destination: values.destino || "",
          origin: values.origen || "",
          provider: values.naviero || "",
        });
      },
    }
  );

  const handleReset = () => {
    resetForm();
    setKeyAutcomplete(keyAutocomplete + 1);
  };

  const openModalUpload = () => {
    setShowModalUpload(true);
  };

  return (
    <Grid container spacing={2}>
      <Grid
        item
        container
        justifyContent="flex-end"
        spacing={4}
        style={{ marginBottom: 10 }}
      >
        {userInfo?.perfil === "1" && (
          <Grid item>
            <Button
              variant="outlined"
              startIcon={<IconUpload />}
              size="large"
              onClick={openModalUpload}
            >
              Subir Archivo XLSX
            </Button>
          </Grid>
        )}
      </Grid>
      <>
        <Grid item container spacing={3}>
          <Grid item xs={12} md={3}>
            <Autocomplete
              key={keyAutocomplete}
              loading={isLoadingProviders}
              options={providers}
              getOptionLabel={(opt) => opt.label}
              getOptionSelected={(opt, val) => opt.value === val.value}
              onChange={(_, val) =>
                setFieldValue("naviero", val?.value || null)
              }
              renderInput={(props) => (
                <TextField
                  {...props}
                  variant="outlined"
                  label="Naviero"
                  placeholder="Naviero"
                  error={Boolean(errors.naviero && touched.naviero)}
                  helperText={
                    Boolean(errors.naviero && touched.naviero) && errors.naviero
                  }
                  InputProps={{
                    ...props.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <IconShip />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <>
                        {isLoadingProviders ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {props.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Autocomplete
              key={keyAutocomplete}
              loading={isLoadingOrigins}
              options={origins.filter((ori) => ori.label !== "UNDEFINED")}
              onChange={(_, val) => setFieldValue("origen", val?.value || null)}
              getOptionLabel={(opt) => opt.label}
              getOptionSelected={(opt, val) => opt.value === val.value}
              renderInput={(props) => (
                <TextField
                  {...props}
                  // {...getFieldProps("origen")}
                  variant="outlined"
                  label="Origen"
                  placeholder="Origen"
                  error={Boolean(errors.origen && touched.origen)}
                  helperText={
                    Boolean(errors.origen && touched.origen) && errors.origen
                  }
                  InputProps={{
                    ...props.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <IconMapPin />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <>
                        {isLoadingOrigins ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {props.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Autocomplete
              key={keyAutocomplete}
              loading={isLoadingDestinations}
              options={destinations.filter((des) => des.label !== "UNDEFINED")}
              onChange={(_, val) =>
                setFieldValue("destino", val?.value || null)
              }
              getOptionLabel={(opt) => opt.label}
              getOptionSelected={(opt, val) => opt.value === val.value}
              renderInput={(props) => (
                <TextField
                  {...props}
                  variant="outlined"
                  label="Destino"
                  placeholder="Destino"
                  error={Boolean(errors.destino && touched.destino)}
                  helperText={
                    Boolean(errors.destino && touched.destino) && errors.destino
                  }
                  InputProps={{
                    ...props.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <IconMapPin />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <>
                        {isLoadingDestinations ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {props.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Autocomplete
              key={keyAutocomplete}
              loading={isLoadingContainers}
              options={containers}
              onChange={(_, val) => {
                setFieldValue("contenedor", val?.value || null);
              }}
              getOptionLabel={(opt) => opt.label}
              getOptionSelected={(opt, val) => opt.value === val.value}
              renderInput={(props) => (
                <TextField
                  variant="outlined"
                  label="Contenedor"
                  placeholder="Contenedor"
                  {...props}
                  error={Boolean(errors.contenedor && touched.contenedor)}
                  helperText={
                    Boolean(errors.contenedor && touched.contenedor) &&
                    errors.contenedor
                  }
                  InputProps={{
                    ...props.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <IconBuildingWarehouse />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <>
                        {isLoadingContainers ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {props.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>
        </Grid>
        <div
          style={{
            display: "flex",
            flex: 1,
            justifyContent: "flex-end",
            margin: "10px 0px",
            marginRight: "10px",
          }}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleSubmit()}
          >
            Visualizar
          </Button>
          &nbsp; &nbsp;
          <Button variant="contained" color="secondary" onClick={handleReset}>
            Limpiar Filtros
          </Button>
        </div>
        <Grid item xs={12}>
          <MainCard variant="outlined">
            {dataCosts ? (
              <CostosContainer
                isLoading={isLoadingCosts || isFetchingCosts}
                // costs={dataCosts ? dataCosts.datos : []}
                // total={dataCosts ? dataCosts.total : ""}
                costs={_costs}
                total={_costs.length.toString()}
              />
            ) : (
              <SelectionMessage />
            )}
          </MainCard>
        </Grid>
      </>
      {showModalUpload && (
        <CustomDialog
          handleClose={() => {
            reset();
            setShowModalUpload(false);
          }}
          isOpen={showModalUpload}
          title="Subir Archivos XLSX"
          maxWidth="sm"
          textButton="Subir"
          hideActionButtons
          isSuccess={isSuccess}
          successMessage={String(data)}
          isError={isError}
          errorMessage="Error al intentar cargar datos. Intente luego."
        >
          <UploadXlsx
            onCancel={() => {
              reset();
              setShowModalUpload(false);
            }}
            isLoading={isLoading}
            onSubmit={(files) => {
              processFile({
                files,
              });
            }}
          />
        </CustomDialog>
      )}
    </Grid>
  );
};
export default Cotizaciones;
